import React, { useEffect, useMemo, useState } from "react";
import { Calendar, Clock, MessageSquare } from "lucide-react";
import {
  useUserBookingQuery,
  useAvailableBookingSlotsQuery,
} from "../../api/bookingApi.ts";
import {
  useCreateChangeRequestMutation,
  useCounterProposeChangeRequestMutation,
  useCancelChangeRequestMutation,
  usePendingChangeRequestQuery,
  BookingChangeRequestCreationDto,
} from "../../api/bookingChangeRequestApi.ts";
import MonthCalendar from "../MonthCalendar.tsx";
import SlotTimePicker from "./SlotTimePicker.tsx";
import ActionButton from "../ActionButton.tsx";
import LoadingState from "../LoadingState.tsx";
import ErrorState from "../ErrorState.tsx";
import { Trans, useLingui } from "@lingui/react/macro";
import { useNavigate } from "react-router";
import { useLanguageStore } from "../../stores/languageStore.ts";
import {
  format,
  startOfDay,
  isSameDay,
  addMinutes,
  differenceInMinutes,
} from "date-fns";

interface MoveBookingProps {
  bookingId: string;
  counterRequestId?: string;
  successRedirectTo: string;
}

interface ProposedSlot {
  date?: Date;
  time?: string;
}

function computeEndTime(
  date: Date,
  time: string,
  durationMinutes: number,
): string {
  const [h, m] = time.split(":").map(Number);
  const start = new Date(date);
  start.setHours(h, m, 0, 0);
  return format(addMinutes(start, durationMinutes), "HH:mm");
}

const MoveBooking: React.FC<MoveBookingProps> = ({
  bookingId,
  counterRequestId,
  successRedirectTo,
}) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { dateFnsLocale } = useLanguageStore();

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------
  const {
    data: booking,
    isLoading: bookingLoading,
    isError: bookingError,
  } = useUserBookingQuery(bookingId);

  const { data: pendingRequest, isLoading: pendingLoading } =
    usePendingChangeRequestQuery(bookingId);

  const { mutate: createChangeRequest, isPending: isCreating } =
    useCreateChangeRequestMutation();
  const { mutate: counterPropose, isPending: isCountering } =
    useCounterProposeChangeRequestMutation();
  const { mutate: cancelChangeRequest, isPending: isCancelling } =
    useCancelChangeRequestMutation();

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [selectedTimeslotId, setSelectedTimeslotId] = useState<string | null>(
    null,
  );
  const [proposedSlot, setProposedSlot] = useState<ProposedSlot | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------
  const bookedTimeslots = useMemo(
    () => booking?.timeSlots.filter((ts) => ts.status === "BOOKED") ?? [],
    [booking],
  );

  useEffect(() => {
    if (bookedTimeslots.length === 1 && !selectedTimeslotId) {
      setSelectedTimeslotId(bookedTimeslots[0].id);
    }
  }, [bookedTimeslots, selectedTimeslotId]);

  const durationMap = useMemo(() => {
    const map = new Map<string, number>();
    bookedTimeslots.forEach((ts) => {
      map.set(ts.id, differenceInMinutes(new Date(ts.to), new Date(ts.from)));
    });
    return map;
  }, [bookedTimeslots]);

  const selectedDuration = selectedTimeslotId
    ? durationMap.get(selectedTimeslotId)
    : undefined;

  // Pending change request that conflicts with the currently selected timeslot.
  // Only relevant in create mode — in counter-proposal mode the pending request
  // IS the one being countered, so there is no conflict to surface.
  const pendingConflict = useMemo(() => {
    if (counterRequestId || !pendingRequest || !selectedTimeslotId) return null;
    return pendingRequest.timeslotChanges.some(
      (tc) => tc.originalTimeslotId === selectedTimeslotId,
    )
      ? pendingRequest
      : null;
  }, [counterRequestId, pendingRequest, selectedTimeslotId]);

  const { data: availableSlots } = useAvailableBookingSlotsQuery(
    booking?.teacherId,
    booking?.locationId,
    selectedDuration,
  );

  const availableDates = useMemo(
    () =>
      new Set(
        availableSlots?.slots.map((s) =>
          format(new Date(s.from), "yyyy-MM-dd"),
        ) ?? [],
      ),
    [availableSlots],
  );

  // ---------------------------------------------------------------------------
  // Pre-fill from pending change request (counter-proposal mode)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!pendingRequest || !counterRequestId || bookedTimeslots.length === 0)
      return;

    // Use the first change if there are multiple (legacy support)
    const change = pendingRequest.timeslotChanges[0];
    if (change) {
      const proposedFrom = new Date(change.proposedFrom);
      setSelectedTimeslotId(change.originalTimeslotId);
      setProposedSlot({
        date: startOfDay(proposedFrom),
        time: format(proposedFrom, "HH:mm"),
      });
    }
  }, [pendingRequest, counterRequestId, bookedTimeslots.length]);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  const getAvailableStartTimes = (selectedDate: Date): string[] => {
    if (!availableSlots || !selectedDate) return [];
    return availableSlots.slots
      .filter((s) => isSameDay(new Date(s.from), selectedDate))
      .map((s) => format(new Date(s.from), "HH:mm"))
      .sort();
  };

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleCancelPendingRequest = () => {
    if (!pendingConflict) return;
    cancelChangeRequest(
      { bookingId, changeRequestId: pendingConflict.id },
      {
        onError: () =>
          setError(
            t`Failed to cancel the existing move request. Please try again.`,
          ),
      },
    );
  };

  const handleSubmit = () => {
    if (
      !booking ||
      !selectedTimeslotId ||
      !proposedSlot?.date ||
      !proposedSlot?.time
    )
      return;
    setError(null);

    const [hours, minutes] = proposedSlot.time.split(":").map(Number);
    const duration = durationMap.get(selectedTimeslotId)!;
    const proposedFrom = new Date(proposedSlot.date);
    proposedFrom.setHours(hours, minutes, 0, 0);
    const proposedTo = addMinutes(proposedFrom, duration);

    const dto: BookingChangeRequestCreationDto = {
      timeslotChanges: [
        {
          originalTimeslotId: selectedTimeslotId,
          proposedFrom: proposedFrom.toISOString(),
          proposedTo: proposedTo.toISOString(),
        },
      ],
      message: message.trim() || undefined,
    };

    const callbacks = {
      onSuccess: () => navigate(successRedirectTo),
      onError: () =>
        setError(t`Failed to submit the change request. Please try again.`),
    };

    if (counterRequestId) {
      counterPropose(
        { bookingId, changeRequestId: counterRequestId, dto },
        callbacks,
      );
    } else {
      createChangeRequest({ bookingId, dto }, callbacks);
    }
  };

  const isFormValid = !!(
    selectedTimeslotId &&
    proposedSlot?.date &&
    proposedSlot?.time &&
    !pendingConflict
  );

  const isPending = isCreating || isCountering || isCancelling;

  // ---------------------------------------------------------------------------
  // Loading / Error states
  // ---------------------------------------------------------------------------
  if (bookingLoading || pendingLoading) {
    return <LoadingState message={t`Loading booking details...`} />;
  }

  if (bookingError || !booking) {
    return <ErrorState />;
  }

  if (bookedTimeslots.length === 0) {
    return (
      <div className="py-8 text-center text-gray-500">
        <Trans>No active timeslots to move.</Trans>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-8">
      {/* Selection of which timeslot to move */}
      <div>
        <h3 className="mb-3 text-sm font-medium text-gray-700">
          <Calendar className="mr-2 inline h-4 w-4" />
          <Trans>Which lesson do you want to move?</Trans>
        </h3>
        <div className="grid gap-2 sm:grid-cols-2">
          {bookedTimeslots.map((ts) => {
            const isSelected = selectedTimeslotId === ts.id;
            return (
              <button
                key={ts.id}
                onClick={() => {
                  setSelectedTimeslotId(ts.id);
                  setProposedSlot(null);
                }}
                className={`flex flex-col rounded-lg border px-4 py-2 text-left text-sm transition-colors ${
                  isSelected
                    ? "border-soluud-primary bg-soluud-info"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <span className="font-medium">
                  {format(new Date(ts.from), "PPP p", {
                    locale: dateFnsLocale,
                  })}
                </span>
                <span className="text-gray-500">
                  {format(new Date(ts.to), "p", { locale: dateFnsLocale })} (
                  {durationMap.get(ts.id)} <Trans>min</Trans>)
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Date / time selection for the selected timeslot */}
      {selectedTimeslotId && (
        <div className="rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col gap-6 md:flex-row">
            {/* Calendar column — its natural height drives the row height on desktop */}
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                <Calendar className="mr-2 inline h-4 w-4" />
                <Trans>New Date</Trans>
              </label>
              <MonthCalendar
                availableDates={availableDates}
                selectedDay={proposedSlot?.date}
                onDayClick={(day: Date) => {
                  setProposedSlot({
                    date: day,
                    time: undefined,
                  });
                }}
                onlyFutureSlots={true}
                focusMonth={proposedSlot?.date}
              />
            </div>

            {/* Time selection column.
                On desktop: content is absolutely positioned so it never contributes
                to the flex row height — the panel stays the calendar's height
                regardless of how many slots are visible.
                On mobile: normal flow with a min-h floor. */}
            <div className="relative min-h-[14rem] flex-1">
              <div className="flex flex-col gap-3 md:absolute md:inset-0 md:overflow-y-auto">
                <label className="shrink-0 text-sm font-medium text-gray-700">
                  <Clock className="mr-2 inline h-4 w-4" />
                  <Trans>New Start Time</Trans>
                  {proposedSlot?.date && (
                    <span className="ml-1 font-normal text-gray-500">
                      –{" "}
                      {format(proposedSlot.date, "P", {
                        locale: dateFnsLocale,
                      })}
                    </span>
                  )}
                </label>

                {proposedSlot?.date ? (
                  (() => {
                    const availableTimes = getAvailableStartTimes(
                      proposedSlot.date,
                    );
                    const slotOptions = availableTimes.map((time) => ({
                      id: time,
                      label: time,
                    }));
                    return availableTimes.length > 0 ? (
                      <SlotTimePicker
                        slots={slotOptions}
                        selectedIds={
                          new Set(proposedSlot.time ? [proposedSlot.time] : [])
                        }
                        onToggle={(id) =>
                          setProposedSlot((prev) => ({ ...prev!, time: id }))
                        }
                      />
                    ) : (
                      <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 p-6">
                        <p className="text-center text-sm text-gray-500">
                          <Trans>
                            No available time slots for the selected date.
                          </Trans>
                        </p>
                      </div>
                    );
                  })()
                ) : (
                  <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 p-6">
                    <p className="text-center text-sm text-gray-500">
                      <Trans>Select a date to see available times.</Trans>
                    </p>
                  </div>
                )}

                {/* Confirmed selection summary */}
                {proposedSlot?.date && proposedSlot?.time && (
                  <div className="shrink-0 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                    <Trans>New time:</Trans>{" "}
                    {format(proposedSlot.date, "PPP", {
                      locale: dateFnsLocale,
                    })}
                    {", "}
                    {proposedSlot.time}
                    {" – "}
                    {computeEndTime(
                      proposedSlot.date,
                      proposedSlot.time,
                      durationMap.get(selectedTimeslotId) || 0,
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Optional message */}
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">
          <MessageSquare className="mr-2 inline h-4 w-4" />
          <Trans>Message (optional)</Trans>
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={t`Add a note for the other party...`}
          className="focus:border-soluud-primary focus:ring-soluud-primary/30 block w-full rounded-lg border border-gray-300 p-3 text-sm transition-colors duration-150 focus:ring-2 focus:outline-none"
          rows={3}
        />
      </div>

      {/* Pending conflict banner */}
      {pendingConflict && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="font-medium">
            <Trans>A move request for this lesson is already pending.</Trans>
          </p>
          <p className="mt-1">
            <Trans>
              Cancel the existing request before submitting a new one, or wait
              for the other party to respond.
            </Trans>
          </p>
          <button
            type="button"
            onClick={handleCancelPendingRequest}
            disabled={isCancelling}
            className="mt-3 rounded-lg border border-amber-400 bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-200 disabled:opacity-50"
          >
            {isCancelling ? (
              <Trans>Cancelling...</Trans>
            ) : (
              <Trans>Cancel existing request</Trans>
            )}
          </button>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Submit */}
      <div className="flex justify-end">
        <ActionButton
          type="button"
          disabled={!isFormValid || isPending}
          isLoading={isPending}
          onClick={handleSubmit}
        >
          {counterRequestId ? (
            <Trans>Submit Counter-Proposal</Trans>
          ) : (
            <Trans>Request Move</Trans>
          )}
        </ActionButton>
      </div>
    </div>
  );
};

export default MoveBooking;
